import Button from '../../../components/form-control/Button';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { updateEntireCustomer } from '../../../api/requests';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, idValidationSchema, showToast } from '../../../utils/miscFunctions';
import { useState } from 'react';
import { Customer } from '../../../models/customer/Customer';
import moment from 'moment';
import { User } from 'lucide-react';
import CustomerInformationForm from './components/CustomerInformationForm';
import { CUSTOMER_TYPE } from '../../../resources/enums';
import { ICustomerType } from '../../../resources/interfaces';

interface EditCustomerProps {
  onProceed: () => void;
  onClose: () => void;
  customer: Customer;
}

const EditCustomer = ({ onProceed, onClose, customer }: EditCustomerProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedCustomerType, setSelectedCustomerType] = useState<ICustomerType | null>(customer?.customerType || null)
  const isBusinessCustomer = selectedCustomerType === CUSTOMER_TYPE.BUSINESS
  const isIndividualCustomer = selectedCustomerType === CUSTOMER_TYPE.INDIVIDUAL


  return (
    <Formik
      initialValues={{
        firstname: customer?.firstName ?? '',
        lastname: customer?.lastName ?? '',
        phone: customer.phone ? getPhoneNumberFromShortCodeFormat(customer?.phone) : '',
        otherPhone: customer.otherPhone ? getPhoneNumberFromShortCodeFormat(customer?.otherPhone) : '',
        emailAddress: customer?.email ?? '',
        address: customer?.address ?? '',
        businessName: customer?.businessName ?? '',
        customerType: customer?.customerType ?? null,
        dateOfBirth: customer?.dateOfBirth ?? '',
        incorporationDate: customer?.incorporationDate ?? '',
        digitalAddress: customer?.digitalAddress ?? '',
        employerName: customer?.employerName ?? '',
        employerAddress: customer?.employerAddress ?? '',
        employerContact: customer?.employerPhone ?? '',
        gender: customer?.gender ?? '',
        idExpiry: customer?.idExpiry ?? '',
        idNumber: customer?.idNumber ?? '',
        idType: customer?.idType ?? '',
        monthlyIncome: customer?.monthlyIncomeBand ?? '',
        occupation: customer?.occupation ?? '',
        industry: customer?.industry ?? '',
        state: customer?.state ?? '',
      }}
      enableReinitialize={true}
      validateOnBlur
      validationSchema={Yup.object().shape({
        //address: Yup.string().required('Address is required'),
        firstname: Yup.string().required(isBusinessCustomer ? 'Contact person first name is required' : 'First name is required'),
        lastname: Yup.string().required(isBusinessCustomer ? 'Contact person last name is required' : 'Last name is required'),
        businessName: isBusinessCustomer ? Yup.string().required('Business name is required').nullable() : Yup.string().notRequired().nullable(),
        phone: Yup.number().required('Phone number is required.'),
        // gender: Yup.string().required('Gender is required.'),
        // occupation: Yup.string().required('Occupation is required.'),
        customerType: Yup.string().required('Customer type is required.').nullable(),
        dateOfBirth: (isIndividualCustomer) ? Yup.date()
          .required('Date of birth is required')
          .max(new Date(), 'Date of birth must be in the past') : Yup.string().notRequired().nullable(),
        incorporationDate: isBusinessCustomer ? Yup.string().required('Incorporation date is required.') : Yup.string().notRequired().nullable(),
        ...idValidationSchema('idExpiry', (isIndividualCustomer)).fields,
      })}
      onSubmit={(values) => {
        const payload: any = {
          ...values,
          phone: values.phone ? getFormattedPhoneNumber(values.phone) : null,
          otherPhone: values.otherPhone ? getFormattedPhoneNumber(values.otherPhone) : null,
          idExpiryDate: values.idExpiry ? moment(values.idExpiry).format('YYYY-MM-DD') : null,
          dateOfBirth: values.dateOfBirth ? moment(values.dateOfBirth).format('YYYY-MM-DD') : null,
          incorporationDate: isBusinessCustomer ? moment(values.incorporationDate).format('YYYY-MM-DD') : null,
          email: values.emailAddress ? values.emailAddress : null,
          businessName: values.businessName ? values.businessName : null,
          address: values.address ? values.address : null,
          customerType: values.customerType ? values.customerType : null,
          digitalAddress: values.digitalAddress ? values.digitalAddress : null,
          employerAddress: values.employerAddress ? values.employerAddress : null,
          employerContact: values.employerContact ? values.employerContact : null,
          employerName: values.employerName ? values.employerName : null,
          firstName: values.firstname ? values.firstname : null,
          gender: values.gender ? values.gender : null,
          idNumber: values.idNumber ? values.idNumber : null,
          idType: values.idType ? values.idType : null,
          lastName: values.lastname ? values.lastname : null,
          monthlyIncome: values.monthlyIncome ? values.monthlyIncome : null,
          occupation: values.occupation ? values.occupation : null,
          industry: values.industry ? values.industry : null,
          state: values.state ? values.state : null,

        };

        const id = customer.id;

        if (id) setLoading(true);
        updateEntireCustomer(id, payload)
          .then((response: any) => {
            showToast.success(response.message ?? 'Customer updated successfully.');
            onProceed();
          })
          .catch((error: any) => {
            showToast.error(error.description ?? 'Something went wrong');
          })
          .finally(() => setLoading(false));
      }}>
      {({ handleSubmit }) => (
        <form className="p-5" onSubmit={handleSubmit}>
          <div className='flex items-center gap-2'>
            <User className='h-7 w-7 text-green-600' />
            <h3 className='text-xl font-semibold dark:text-darkBg-mainText'>
              Edit Customer Info
            </h3>
          </div>

          <CustomerInformationForm
            customerData={customer || null}
            customerType={selectedCustomerType || null}
            isRideSharing={false}
            setSelectedCustomerTypeValue={(value)=>setSelectedCustomerType(value)}
          />

          <div className="w-full flex gap-4 !mt-8">
            <Button
              type="button"
              label="Cancel"
              onClick={onClose}
              bgColor='secondary'
              fullWidth
            />
            <Button
              label="Save"
              onClick={handleSubmit}
              bgColor='primary'
              fullWidth
              disabled={loading}
              loading={loading}
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default EditCustomer;
